import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import { getData, postData, getRemoteUrl } from './request'
import { addCases } from './utils'
import { getLoginToken } from './store'
import { getCurrentSessionGeneration, sessionInvalidated } from './session'

function initialState() {
    return {
        workouts: undefined,
        loading: false,
        error: undefined,
        selectedWorkout: undefined,
        createStatus: 'idle',
        createError: undefined,
        saveStatus: 'idle',
        saveError: undefined,
        savedActivity: undefined,
        sessionGeneration: 0,
    }
}

function reqWorkouts() {
    const sessionGeneration = getCurrentSessionGeneration()
    const params = {
        method: 'GET',
        headers: {
            'Authorization': `Bearer ${getLoginToken()}`
        }
    }
    return getData(getRemoteUrl('workouts'), params)
        .then(workouts => ({ sessionGeneration, workouts: workouts || [] }))
}

function reqCreateWorkout(data) {
    return postData(getRemoteUrl('workouts'), data, getLoginToken())
}

function reqSaveLiveActivity(data) {
    return postData(getRemoteUrl('activities/live'), data, getLoginToken())
        .then(json => json || {})
}

export function setWorkoutsData(state, { payload }) {
    if (payload.sessionGeneration !== state.sessionGeneration) return
    state.workouts = payload.workouts
    state.loading = false
    state.error = undefined
}

function setWorkoutsLoading(state) {
    state.loading = true
}

function setWorkoutsError(state, { payload }) {
    if (payload && payload.sessionGeneration !== state.sessionGeneration) return
    state.workouts = []
    state.loading = false
    state.error = (payload && payload.error) || undefined
}

function setSaveSucceeded(state, { payload }) {
    state.saveStatus = 'saved'
    state.saveError = undefined
    state.savedActivity = payload
}

function setSavePending(state) {
    state.saveStatus = 'saving'
    state.saveError = undefined
}

function setSaveFailed(state, { error }) {
    state.saveStatus = 'error'
    state.saveError = error
}

function setCreateSucceeded(state) {
    state.createStatus = 'success'
    state.createError = undefined
}

function setCreatePending(state) {
    state.createStatus = 'loading'
    state.createError = undefined
}

function setCreateFailed(state, { error }) {
    state.createStatus = 'error'
    state.createError = error
}

export const asyncReqWorkouts = createAsyncThunk(
    'asyncReqWorkouts',
    async (_, { rejectWithValue }) => {
        try {
            return await reqWorkouts()
        } catch (error) {
            return rejectWithValue({ sessionGeneration: getCurrentSessionGeneration(), error })
        }
    }
)
export const asyncCreateWorkout = createAsyncThunk('asyncCreateWorkout', reqCreateWorkout)
export const asyncSaveLiveActivity = createAsyncThunk('asyncSaveLiveActivity', reqSaveLiveActivity)

export const workoutsSlice = createSlice({
    name: 'workouts',
    initialState: initialState(),

    reducers: {
        setSelectedWorkout(state, { payload }) {
            state.selectedWorkout = payload
        },
        resetCreateStatus(state) {
            state.createStatus = 'idle'
            state.createError = undefined
        },
        resetSaveStatus(state) {
            state.saveStatus = 'idle'
            state.saveError = undefined
            state.savedActivity = undefined
        }
    },

    extraReducers: builder => {
        addCases(builder, asyncReqWorkouts, setWorkoutsData, setWorkoutsLoading, setWorkoutsError)
        addCases(builder, asyncCreateWorkout, setCreateSucceeded, setCreatePending, setCreateFailed)
        addCases(builder, asyncSaveLiveActivity, setSaveSucceeded, setSavePending, setSaveFailed)
        builder.addCase(sessionInvalidated, (state, action) => ({
            ...initialState(),
            sessionGeneration: action.payload,
        }))
    }
})

export const { setSelectedWorkout, resetCreateStatus, resetSaveStatus } = workoutsSlice.actions